import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import axios from 'axios';
import { buildApiUrl } from '../config/api';
import '../styles/ai-chat-widget.css';

const STORAGE_SESSION_KEY = 'ai_assistant_session_id';
const STORAGE_GUEST_KEY = 'ai_assistant_guest_id';
const STORAGE_PHONE_KEY = 'ai_assistant_phone';

const WELCOME_MESSAGE = {
    id: 'welcome',
    role: 'assistant',
    content: 'Hi, I am RedGoTrip AI. I can help you find packages, destinations, camping stays, and booking guidance. Tell me your budget, destination, or travel plan.'
};

const MARKDOWN_LINK_PATTERN = /\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)/g;
const URL_PATTERN = /(https?:\/\/[^\s]+)/g;

const AIChatWidget = () => {
    const location = useLocation();
    const messagesEndRef = useRef(null);
    const isAdminPage = location.pathname.startsWith('/admin');
    const [isOpen, setIsOpen] = useState(false);
    const [input, setInput] = useState('');
    const [loading, setLoading] = useState(false);
    const [messages, setMessages] = useState([WELCOME_MESSAGE]);
    const [suggestions, setSuggestions] = useState([
        'Best Kashmir package under 20000',
        'Suggest honeymoon trip',
        'Best camping stays for family'
    ]);
    const [phoneInput, setPhoneInput] = useState('');
    const [phoneError, setPhoneError] = useState('');
    const [verifiedPhone, setVerifiedPhone] = useState(() => localStorage.getItem(STORAGE_PHONE_KEY) || '');

    const guestId = useMemo(() => {
        let existing = localStorage.getItem(STORAGE_GUEST_KEY);
        if (!existing) {
            existing = (window.crypto?.randomUUID?.() || `guest_${Date.now()}`);
            localStorage.setItem(STORAGE_GUEST_KEY, existing);
        }
        return existing;
    }, []);

    const sessionId = localStorage.getItem(STORAGE_SESSION_KEY);

    useEffect(() => {
        if (isAdminPage || !isOpen || !sessionId) return;

        const loadSession = async () => {
            try {
                const response = await axios.get(buildApiUrl(`/assistant/session/${sessionId}`));
                const sessionMessages = response.data?.data?.messages || [];

                if (sessionMessages.length > 0) {
                    setMessages(sessionMessages);
                }
            } catch (error) {
                console.error('Assistant session load error:', error);
            }
        };

        loadSession();
    }, [isAdminPage, isOpen, sessionId]);

    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [messages, loading]);

    const submitMessage = async (customMessage) => {
        if (isAdminPage) return;
        const nextMessage = String(customMessage || input).trim();
        if (!nextMessage || loading) return;

        const optimisticUserMessage = {
            id: `local_${Date.now()}`,
            role: 'user',
            content: nextMessage
        };

        setMessages((prev) => [...prev, optimisticUserMessage]);
        setInput('');
        setLoading(true);

        try {
            const response = await axios.post(buildApiUrl('/assistant/chat'), {
                message: nextMessage,
                session_id: localStorage.getItem(STORAGE_SESSION_KEY),
                guest_id: guestId,
                source_page: location.pathname,
                phone_number: verifiedPhone || null
            });

            const payload = response.data?.data || {};
            if (payload.session_id) {
                localStorage.setItem(STORAGE_SESSION_KEY, payload.session_id);
            }

            if (Array.isArray(payload.messages) && payload.messages.length > 0) {
                setMessages(payload.messages);
            } else if (payload.reply) {
                setMessages((prev) => [...prev, { id: `reply_${Date.now()}`, role: 'assistant', content: payload.reply }]);
            }

            if (Array.isArray(payload.suggestions) && payload.suggestions.length > 0) {
                setSuggestions(payload.suggestions);
            }
        } catch (error) {
            console.error('Assistant chat error:', error);
            setMessages((prev) => [
                ...prev,
                {
                    id: `error_${Date.now()}`,
                    role: 'assistant',
                    content: 'I am having trouble right now. Please try again in a moment or contact RedGoTrip support at +91 9335444047.'
                }
            ]);
        } finally {
            setLoading(false);
        }
    };

    const handlePhoneSubmit = (e) => {
        e.preventDefault();
        const normalizedPhone = normalizeIndianPhone(phoneInput);

        if (!normalizedPhone) {
            setPhoneError('Please enter a valid 10-digit mobile number.');
            return;
        }

        localStorage.setItem(STORAGE_PHONE_KEY, normalizedPhone);
        setVerifiedPhone(normalizedPhone);
        setPhoneInput('');
        setPhoneError('');
    };

    if (isAdminPage) {
        return null;
    }

    return (
        <div className={`ai-chat-widget ${isOpen ? 'open' : ''}`}>
            {isOpen && (
                <div className="ai-chat-panel">
                    <div className="ai-chat-header">
                        <div>
                            <span className="ai-chat-kicker">RedGoTrip Assistant</span>
                            <h3>Ask RedGoTrip AI</h3>
                        </div>
                        <button type="button" className="ai-chat-close" onClick={() => setIsOpen(false)} aria-label="Close assistant">
                            <i className="fas fa-times"></i>
                        </button>
                    </div>

                    <div className="ai-chat-body">
                        {messages.map((message) => (
                            <div key={message.id || `${message.role}_${message.created_at || message.content}`} className={`ai-chat-message ${message.role}`}>
                                <div className="ai-chat-bubble">
                                    {renderMessageContent(message.content)}
                                </div>
                            </div>
                        ))}

                        {!verifiedPhone && (
                            <div className="ai-chat-verification-card">
                                <h4>Continue with your mobile number</h4>
                                <p>
                                    Please enter your correct mobile number first so our assistant can guide you better and
                                    our team can help if needed.
                                </p>
                                <form className="ai-chat-phone-form" onSubmit={handlePhoneSubmit}>
                                    <label htmlFor="ai-chat-phone">Mobile Number</label>
                                    <input
                                        id="ai-chat-phone"
                                        type="tel"
                                        inputMode="numeric"
                                        autoComplete="tel"
                                        placeholder="Enter 10-digit mobile number"
                                        value={phoneInput}
                                        onChange={(e) => {
                                            setPhoneInput(e.target.value);
                                            if (phoneError) {
                                                setPhoneError('');
                                            }
                                        }}
                                        maxLength={14}
                                    />
                                    {phoneError ? <p className="ai-chat-phone-error">{phoneError}</p> : null}
                                    <button type="submit">Start Chat</button>
                                </form>
                            </div>
                        )}

                        {loading && (
                            <div className="ai-chat-message assistant">
                                <div className="ai-chat-bubble typing">
                                    <span></span>
                                    <span></span>
                                    <span></span>
                                </div>
                            </div>
                        )}
                        <div ref={messagesEndRef}></div>
                    </div>

                    <div className="ai-chat-suggestions">
                        {suggestions.map((suggestion) => (
                            <button
                                key={suggestion}
                                type="button"
                                className="ai-suggestion-chip"
                                onClick={() => submitMessage(suggestion)}
                                disabled={!verifiedPhone}
                            >
                                {suggestion}
                            </button>
                        ))}
                    </div>

                    <form
                        className="ai-chat-input-row"
                        onSubmit={(e) => {
                            e.preventDefault();
                            submitMessage();
                        }}
                    >
                        <input
                            type="text"
                            value={input}
                            onChange={(e) => setInput(e.target.value)}
                            placeholder={verifiedPhone ? 'Ask about packages, camping, destinations...' : 'Enter mobile number first'}
                            disabled={loading || !verifiedPhone}
                        />
                        <button type="submit" disabled={loading || !input.trim() || !verifiedPhone}>
                            <i className={`fas ${loading ? 'fa-spinner fa-spin' : 'fa-paper-plane'}`}></i>
                        </button>
                    </form>
                </div>
            )}

            <button type="button" className="ai-chat-toggle" onClick={() => setIsOpen((prev) => !prev)} aria-label="Open travel assistant">
                <i className="fas fa-robot"></i>
                <span>Chat with us</span>
            </button>
        </div>
    );
};

function renderMessageContent(content) {
    const text = String(content || '');
    const lines = text.split('\n');

    return lines.map((line, lineIndex) => (
        <React.Fragment key={`line_${lineIndex}`}>
            {renderLineContent(line)}
            {lineIndex < lines.length - 1 ? <br /> : null}
        </React.Fragment>
    ));
}

function renderLineContent(line) {
    const nodes = [];
    let cursor = 0;
    let matchIndex = 0;
    let match;

    while ((match = MARKDOWN_LINK_PATTERN.exec(line)) !== null) {
        const [fullMatch, label, href] = match;
        const start = match.index;

        if (start > cursor) {
            nodes.push(...renderPlainTextWithLinks(line.slice(cursor, start), `plain_${matchIndex}`));
        }

        nodes.push(
            <a
                key={`md_${matchIndex}_${href}`}
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                className="ai-chat-link"
            >
                {label}
            </a>
        );

        cursor = start + fullMatch.length;
        matchIndex += 1;
    }

    if (cursor < line.length) {
        nodes.push(...renderPlainTextWithLinks(line.slice(cursor), `tail_${matchIndex}`));
    }

    MARKDOWN_LINK_PATTERN.lastIndex = 0;
    return nodes.length ? nodes : line;
}

function renderPlainTextWithLinks(text, keyPrefix) {
    const parts = [];
    let cursor = 0;
    let urlIndex = 0;
    let match;

    while ((match = URL_PATTERN.exec(text)) !== null) {
        const href = match[0];
        const start = match.index;

        if (start > cursor) {
            parts.push(text.slice(cursor, start));
        }

        parts.push(
            <a
                key={`${keyPrefix}_url_${urlIndex}`}
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                className="ai-chat-link"
            >
                {href}
            </a>
        );

        cursor = start + href.length;
        urlIndex += 1;
    }

    if (cursor < text.length) {
        parts.push(text.slice(cursor));
    }

    URL_PATTERN.lastIndex = 0;
    return parts.length ? parts : [text];
}

function normalizeIndianPhone(value) {
    const digits = String(value || '').replace(/\D/g, '');
    const normalized = digits.startsWith('91') && digits.length === 12 ? digits.slice(2) : digits;

    if (/^[6-9]\d{9}$/.test(normalized)) {
        return normalized;
    }

    return '';
}

export default AIChatWidget;
